<!-- TEST DATA 
{
    "MemberNumber": 297063,
    "DependentCode": 1,
    "DateOfBirth": "1963-09-05",
    "firstName": "David",
    "surname": "Tucker",
    "phoneNumber": "0400123456"
} -->
<template>
    <div v-if="!disabled" :class="{ 'is-loading': isLoading }" class="md:min-h-112">
        <Tabs :active-step="step" :steps="steps" @step-to="stepTo" />

        <div class="relative -mb-2 transition duration-500 loading-bar"></div>

        <div v-if="ui" class="pb-2 sm:py-20 bg-grey-100">
            <transition name="slide" mode="out-in">
                <Service
                    v-if="step == 0"
                    :ui="ui"
                    :service-type="serviceType"
                    :service-questions="serviceQuestions"
                    :is-loading="isLoading"
                    @submit-data="setDataFromObject"
                    @complete="completeStepService"
                />

                <Location
                    v-if="step == 1"
                    :ui="ui"
                    :service-type="serviceType"
                    :locations="locations"
                    :is-loading="isLoading"
                    @submit-data="setDataFromObject"
                    @complete="completeStepLocation"
                />

                <Selection
                    v-if="step == 2"
                    :ui="ui"
                    :service-type="serviceType"
                    :practitioners="practitioners"
                    :appointments="appointments"
                    :is-loading="isLoading"
                    :appointment-id="appointmentId"
                    :weekday="weekday"
                    @update-weekday="updateWeekday($event)"
                    @update-date-range="updateDateRange($event)"
                    @update-practitioner="updatePractitioner($event)"
                    @back="stepTo(1)"
                    @select-appointment="appointmentId = $event"
                    @submit-data="setDataFromObject"
                    @complete="completeStepSelection"
                />

                <Details
                    v-if="step == 3"
                    :ui="ui"
                    :service-type="serviceType"
                    :details="details"
                    :is-loading="isLoading"
                    :alerts="alerts"
                    @complete="completeStepDetails"
                    @step-to="stepTo"
                />

                <Review
                    v-if="step == 4"
                    :ui="ui"
                    :service-type="serviceType"
                    :details="details"
                    :location="selectedLocation"
                    :appointment="selectedAppointment"
                    :practitioner="selectedPractitioner"
                    :is-loading="isLoading"
                    :new-patient="newPatient"
                    @complete="completeStepReview"
                    @step-to="stepTo"
                />
            </transition>
        </div>
        <Modals v-if="ui" :modals="ui.modals" />
        <AlertBubbles v-if="!alertBubblesDisabled" :alerts="alerts" />
    </div>
</template>

<script>
import { trackSelection, trackLocation, trackStep, trackSubmission } from './utils/tracking';
import * as apiService from '@/../vue/online-booking/utils/api';
// import * as stubs from '@/../vue/online-booking/utils/stubs';
import { toDateString, toTimeString } from './utils/dates';
import scrollTop from '@/../vue/online-booking/utils/scroll';
import Modals from '@/../vue/online-booking/Modals.vue';
import Tabs from '@/../vue/online-booking/components/Tabs.vue';
import AlertBubbles from '@/../vue/online-booking/components/AlertBubbles.vue';
import Service from '@/../vue/online-booking/steps/service/index.vue';
import Location from '@/../vue/online-booking/steps/location/index.vue';
import Selection from '@/../vue/online-booking/steps/selection/index.vue';
import Details from '@/../vue/online-booking/steps/details/index.vue';
import Review from '@/../vue/online-booking/steps/review/index.vue';
import bookings from '@/../vue/online-booking/mixins/bookings';
import localDateTime from '@/../vue/online-booking/mixins/local-date-time';
import alerts from '@/../vue/online-booking/mixins/alerts';

export default {
    components: {
        Tabs,
        Service,
        Location,
        Selection,
        Details,
        Review,
        Modals,
        AlertBubbles
    },

    mixins: [bookings, localDateTime, alerts],

    data: () => ({
        disabled: false,
        serviceQuestions: {},
        details: {},
        step: 0,
        ui: undefined,
        steps: ['Service', 'Location', 'Selection', 'Details', 'Review']
    }),

    computed: {
        alertBubblesDisabled() {
            // Details page displays errors within the form UI
            return this.step === 3;
        }
    },

    async created() {
        // this.serviceQuestions = stubs.serviceQuestions;
        const systemStatus = await apiService.checkStatus();
        if (systemStatus.data.data.status === 'Offline') {
            this.disabled = true;
            document.querySelector('#online-booking-disabled').classList.remove('hidden');
        }
        this.ui = await this.loadWhile(apiService.getUi());
        trackStep(this.steps[this.step]);
    },

    methods: {
        async completeStepService() {
            await this.createBooking(this.serviceType.toLowerCase());
            this.stepTo(this.step + 1);
        },

        async completeStepLocation() {
            await this.updateBooking();
            trackLocation(this.selectedLocation.locationName);
            this.stepTo(this.step + 1);
        },

        async completeStepSelection() {
            await this.holdBooking();
            trackSelection(
                toDateString(this.selectedAppointment.appointmentDate),
                toTimeString(this.selectedAppointment.appointmentTime)
            );
            this.stepTo(this.step + 1);
        },

        async completeStepDetails(data) {
            Object.keys(data).forEach((k) => {
                this.$set(this.details, k, data[k].trim());
            });
            await this.validateDetails();
            this.stepTo(this.step + 1);
        },

        async completeStepReview() {
            const res = await this.completeBooking();
            console.log(res);
            this.isLoading = true;
            trackSubmission(
                toDateString(this.selectedAppointment.appointmentDate),
                toTimeString(this.selectedAppointment.appointmentTime),
                this.selectedLocation.locationName,
                this.serviceType
            );
            window.localStorage.setItem(
                'booking',
                JSON.stringify({
                    ...this._data,
                    selectedLocation: this.selectedLocation,
                    selectedAppointment: this.selectedAppointment,
                    selectedPractitioner: this.selectedPractitioner
                })
            );

            window.location.href = `/${this.serviceType.toLowerCase()}-booking-confirmation?id=${
                this.appointmentId
            }`;
        },

        setDataFromObject(keyValueObj) {
            // Util to iterate through and set data fields
            Object.keys(keyValueObj).forEach((k) => {
                this[k] = keyValueObj[k];
            });
        },

        stepTo(step) {
            this.clearAlerts();
            const steppingBack = step < this.step;
            const appointmentHolded = this.step > 2;
            if (steppingBack && appointmentHolded && [0, 1, 2].includes(step)) {
                // Release any booking when stepping back to re-selecting booking.
                this.releaseBooking();
            }
            this.step = step;
            scrollTop();
            trackStep(this.steps[this.step]);
        }
    }
};
</script>

<style scoped>
.booking-slide {
    @apply bg-white px-3 py-14;

    @screen sm {
        @apply px-12 pt-16 pb-14 shadow-md max-w-md mx-auto rounded-md;
    }
}
</style>
